import { useQuery } from '@apollo/client';
import { Edit } from '@mui/icons-material';
import { Box, Button, DialogActions, Typography, type DialogProps } from '@mui/material';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDeleteEvent } from '~/api/events/delete';
import { Tooltip } from '~/components/Tooltip';
import { DeleteButton } from '~/components/button';
import { Count } from '~/components/count';
import { Dialog, DialogTitle } from '~/components/dialogs/components';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { DialogContent } from '~/components/dialogs/lib';
import { SearchBar } from '~/components/forms/search-bar';
import { SearchContainer } from '~/components/search';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import { formatDate } from '~/lib/datetime';
import { searchRegex } from '~/lib/search';
import { EditEventDialog } from '~/pages/event-types/detail/components/edit-event-dialog';
import {
  NetworkSchedulerEventDocument,
  type NetworkSchedulerEvent__Event as Event,
} from '../queries/event.generated';
import { DeviceGroupsTable } from './device-groups';

export interface EventDialogProps extends Omit<DialogProps, 'children'> {
  close: () => void;
  eventId: number;
  open: boolean;
}

export const EventDialog = ({ close, eventId, open }: EventDialogProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const [search, setSearch] = useState('');
  const [editOpen, setEditOpen] = useState(false);

  const [deleteEvent, { loading: deleting }] = useDeleteEvent();
  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const { data } = useQuery(NetworkSchedulerEventDocument, {
    variables: { networkId: currentNetwork.id, eventId: eventId },
  });

  const searchReg = searchRegex(search);

  const rows = data?.network?.event.eventType.showDeviceGroups?.filter((item) =>
    searchReg.test(item.deviceGroup.name),
  );

  const event = data?.network?.event;
  const eventName = event?.name || '';

  // Format dates separately for translation interpolation
  const eventDateText = (event: Event) => {
    const start = DateTime.fromJSDate(new Date(event.start));
    const stop = DateTime.fromJSDate(new Date(event.stop));
    const startDate = formatDate(event.start, {
      format: DateTime.DATETIME_MED,
      stripZeroMinutes: true,
    });
    const endDate = formatDate(event.stop, {
      format: start.hasSame(stop, 'day') ? DateTime.TIME_SIMPLE : DateTime.DATETIME_MED,
      stripZeroMinutes: true,
    });
    return { startDate, endDate };
  };

  const { startDate, endDate } = event ? eventDateText(event) : { startDate: '', endDate: '' };

  const handleDelete = async () => {
    if (!event || !(await confirmDelete())) return;
    const result = await deleteEvent({ variables: { ids: event.id } });
    if (result.data?.deleteEvent?.deletedIds.length) close();
  };

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={close}>
      <DialogTitle onClose={close}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            <img alt="" width="50" src={data?.network?.event.eventType.iconFile?.uri}></img>
          </Box>
          {data?.network?.event &&
            t('pages.events.dialog.title', { eventName, startDate, endDate })}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mt: 2 }}>
            {t('pages.events.dialog.description')}
          </Typography>
        </Box>

        {data?.network?.event && (
          <>
            <SearchContainer>
              <SearchBar
                onChange={setSearch}
                placeholder={t('pages.events.dialog.search_placeholder')}
                search={search}
              />
              <Count
                selectedCount={0}
                totalCount={rows?.length ?? 0}
                thing="common.pluralize.device_group_plural"
              />
            </SearchContainer>
            <Box sx={{ minHeight: 250 }}>
              <DeviceGroupsTable
                showEventTypeDeviceGroups={rows ?? []}
                event={data.network.event}
              />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={close} variant="outlined">
          {t('common.dialogs.close')}
        </Button>
        {event && (
          <>
            <Tooltip title={event.canDestroy.reasons?.fullMessages.join(', ') ?? ''}>
              <span>
                <DeleteButton
                  disabled={deleting || !event.canDestroy.value}
                  onClick={handleDelete}
                />
              </span>
            </Tooltip>
            <Tooltip title={event.canUpdate.reasons?.fullMessages.join(', ') ?? ''}>
              <span>
                <Button
                  color="primary"
                  disabled={!event.canUpdate.value}
                  onClick={() => setEditOpen(true)}
                  startIcon={<Edit />}
                  variant="contained"
                >
                  {t('common.buttons.edit')}
                </Button>
              </span>
            </Tooltip>
          </>
        )}
      </DialogActions>

      {event && (
        <>
          <EditEventDialog close={() => setEditOpen(false)} event={event} open={editOpen} />
          <ConfirmDialog
            {...confirmDeleteProps}
            confirm={t('common.dialogs.delete')}
            deleteConfirm
            prompt={
              <Trans
                components={{ b: <b /> }}
                i18nKey="pages.event_types.events_tab.table.delete_confirm_prompt"
                values={{ eventName: event.name, eventTypeName: event.eventType.name }}
              />
            }
            title={t('pages.event_types.events_tab.table.delete_confirm_title')}
          />
        </>
      )}
    </Dialog>
  );
};
